import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Loader2, Truck } from 'lucide-react';
import purchaseService from '../../services/purchaseService';
import subcontractService from '../../services/subcontractService';

function CreateSubcontractDialog({
  open,
  onOpenChange,
  productionOrder,
  onSuccess,
}) {
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Collect all available subcontract steps from all items
  const availableSteps = useMemo(() => {
    if (!productionOrder) return [];
    const items = productionOrder.items || [];
    const steps = [];
    items.forEach((item, itemIndex) => {
      const processSteps = item.process_steps || [];
      processSteps.forEach((step, stepIndex) => {
        const availableQty = (step.quantity_pending || 0) - (step.subcontract_quantity || 0);
        if (availableQty > 0 && step.status !== 'completed') {
          // Check previous step constraint
          if (step.sequence > 1) {
            const prevStep = processSteps.find(s => s.sequence === step.sequence - 1);
            if (prevStep && prevStep.status !== 'completed') return;
          }
          steps.push({
            key: `${itemIndex}-${step.sequence}`,
            itemIndex,
            stepIndex,
            stepSequence: step.sequence,
            stepName: step.name,
            finishedProductCode: item.finished_product_code,
            finishedProductName: item.finished_product_name,
            plannedQuantity: item.planned_quantity,
            availableQuantity: availableQty,
            quantity: availableQty,
            selected: false
          });
        }
      });
    });
    return steps;
  }, [productionOrder]);

  const [selectedSteps, setSelectedSteps] = useState([]);

  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    unit_price: '',
    expected_return_date: '',
    notes: ''
  });

  useEffect(() => {
    if (open) {
      setFormData({
        supplier_id: '',
        supplier_name: '',
        unit_price: '',
        expected_return_date: '',
        notes: ''
      });
      setSelectedSteps([]);
      setError(null);
      loadSuppliers();
    }
  }, [open]);

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const result = await purchaseService.getSuppliers(1, 100);
      const supplierList = Array.isArray(result) ? result : (result?.data?.items || result?.items || []);
      setSuppliers(supplierList);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleSupplierChange = (value) => {
    const supplier = suppliers.find(s => s._id === value || s.supplier_id === value);
    setFormData(prev => ({
      ...prev,
      supplier_id: value,
      supplier_name: supplier?.supplier_name || supplier?.name || ''
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleStep = (key) => {
    setSelectedSteps(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleSubmit = async () => {
    if (!formData.supplier_id) {
      setError('请选择供应商');
      return;
    }
    if (selectedSteps.length === 0) {
      setError('请至少选择一个工序');
      return;
    }

    const selectedItems = availableSteps.filter(s => selectedSteps.includes(s.key));

    // Group by step name - all selected steps should have same step name
    const stepName = selectedItems[0]?.stepName;
    const stepSequence = selectedItems[0]?.stepSequence;

    setSubmitting(true);
    setError(null);

    try {
      const items = selectedItems.map(s => ({
        production_order_id: productionOrder._id,
        production_order_number: productionOrder.production_order_id,
        item_index: s.itemIndex,
        finished_product_code: s.finishedProductCode,
        finished_product_name: s.finishedProductName,
        planned_quantity: s.availableQuantity,
        unit_price: parseFloat(formData.unit_price) || 0,
        notes: formData.notes
      }));

      const data = {
        supplier_id: formData.supplier_id,
        supplier_name: formData.supplier_name,
        step_name: stepName,
        step_sequence: stepSequence,
        items,
        expected_return_date: formData.expected_return_date,
        notes: formData.notes
      };

      const result = await subcontractService.createSubcontractOrder(data);

      if (onSuccess) {
        onSuccess(result);
      }
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to create subcontract:', err);
      setError(err.response?.data?.error || err.message || '创建外协单失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-amber-600" />
            创建外协单
          </DialogTitle>
          <DialogDescription>
            工单: {productionOrder?.production_order_id} | 请勾选需要外协的工序
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="sc-supplier">供应商 <span className="text-red-500">*</span></Label>
            <Select
              value={formData.supplier_id}
              onValueChange={handleSupplierChange}
              disabled={loadingSuppliers || submitting}
            >
              <SelectTrigger id="sc-supplier">
                <SelectValue placeholder={loadingSuppliers ? '加载中...' : '选择供应商'} />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map(supplier => (
                  <SelectItem
                    key={supplier._id || supplier.supplier_id}
                    value={supplier._id || supplier.supplier_id}
                  >
                    {supplier.supplier_name || supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Step Selection Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">选择</TableHead>
                  <TableHead>产品</TableHead>
                  <TableHead>工序</TableHead>
                  <TableHead className="text-center">可外协数量</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {availableSteps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                      暂无可外协的工序
                    </TableCell>
                  </TableRow>
                ) : (
                  availableSteps.map((s) => (
                    <TableRow key={s.key} className={selectedSteps.includes(s.key) ? 'bg-amber-50' : ''}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedSteps.includes(s.key)}
                          onChange={() => toggleStep(s.key)}
                          className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{s.finishedProductCode}</div>
                        <div className="text-xs text-muted-foreground">{s.finishedProductName}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{s.stepName}</Badge>
                      </TableCell>
                      <TableCell className="text-center">{s.availableQuantity}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sc-unit-price">加工单价 (元/件)</Label>
            <Input
              id="sc-unit-price"
              type="number"
              min="0"
              step="0.01"
              value={formData.unit_price}
              onChange={(e) => handleChange('unit_price', e.target.value)}
              placeholder="0.00"
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sc-return-date">计划交期</Label>
            <Input
              id="sc-return-date"
              type="date"
              value={formData.expected_return_date}
              onChange={(e) => handleChange('expected_return_date', e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sc-notes">备注</Label>
            <Input
              id="sc-notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="外协备注..."
              disabled={submitting}
            />
          </div>

          {selectedSteps.length > 0 && formData.unit_price && (
            <div className="text-sm text-muted-foreground bg-gray-50 p-2 rounded">
              已选 {selectedSteps.length} 个工序，预计金额: {(
                availableSteps
                  .filter(s => selectedSteps.includes(s.key))
                  .reduce((sum, s) => sum + s.availableQuantity * (parseFloat(formData.unit_price) || 0), 0)
              ).toFixed(2)} 元
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !formData.supplier_id}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                创建中...
              </>
            ) : (
              <>创建外协单</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateSubcontractDialog;
